/**
 * Pure display helpers for the creative lifecycle state (Path C, Slice 9).
 * Maps the wire enum to a label + an MUI chip color so AssetCard and the
 * preview modal render a consistent chip.
 */

import type { LifecycleState } from '../../domain';

/** MUI <Chip> color names we map lifecycle states onto. */
export type LifecycleChipColor =
  | 'default'
  | 'info'
  | 'success'
  | 'warning';

export interface LifecycleMeta {
  label: string;
  color: LifecycleChipColor;
}

const LIFECYCLE_META: Record<LifecycleState, LifecycleMeta> = {
  Draft: { label: 'Draft', color: 'default' }, // gray
  Refined: { label: 'Refined', color: 'info' }, // blue
  Finalized: { label: 'Finalized', color: 'success' }, // green
  Published: { label: 'Published', color: 'warning' }, // gold
  Archived: { label: 'Archived', color: 'default' }, // muted
};

/**
 * Display metadata for a lifecycle state. Unknown / missing states fall back
 * to a neutral "Draft"-style chip so the UI never renders an empty chip.
 */
export function lifecycleMeta(state?: string | null): LifecycleMeta {
  if (state && state in LIFECYCLE_META) {
    return LIFECYCLE_META[state as LifecycleState];
  }
  return { label: state?.trim() || 'Draft', color: 'default' };
}
